import { ExternalLink, Mail, RefreshCw } from "lucide-react";
import useSEO from "@/hooks/useSEO";
import { useTrackPageView } from "@/lib/analytics";
import KineticTitle, { HeroFade } from "@/components/books/KineticTitle";
import Reveal from "@/components/books/Reveal";
import { LEVEL3_BOOKS, LEVEL3_SUPPORT, LEVEL3_ACCENT as L } from "@/data/level3Books";

const BOOK_RESOURCES = {
  "money-how-it-really-works": [
    { name: "Reserve Bank of India (RBI)", detail: "Official guidance on banking, credit and consumer protection in India.", url: "https://www.rbi.org.in" },
    { name: "Income Tax Department, India", detail: "Official source for current income tax rules, slabs and filing.", url: "https://www.incometax.gov.in" },
  ],
  "money-that-grows": [
    { name: "Securities and Exchange Board of India (SEBI)", detail: "Official regulator for investing, mutual funds and the securities market in India.", url: "https://www.sebi.gov.in" },
    { name: "Reserve Bank of India (RBI)", detail: "Official guidance on banking and credit.", url: "https://www.rbi.org.in" },
  ],
};

export default function Level3Updates() {
  useTrackPageView("level3_updates_view");
  useSEO({
    title: "Updates & Sources — LifeIQ Level 3 · Life Design",
    description: "Current guidance, review dates and official reference sources for the LifeIQ Level 3 · Life Design 5-book series.",
    canonicalPath: "/books/level3/updates",
  });

  return (
    <main>
      <section style={{ backgroundColor: L.navy }}>
        <div className="max-w-[900px] mx-auto px-5 sm:px-8 pt-16 md:pt-24 pb-16 md:pb-20">
          <HeroFade delay={0}>
            <div className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.24em] font-semibold" style={{ color: L.gold }}>
              <RefreshCw size={14} /> Updates &amp; sources
            </div>
          </HeroFade>
          <KineticTitle
            testId="level3-updates-title"
            className="mt-4 font-serif-d text-[30px] sm:text-[44px] leading-[1.1] text-white"
            lines={["Staying current, book by book."]}
          />
          <HeroFade delay={0.2}>
            <p className="mt-6 text-[16px] text-white/80 leading-[1.7] max-w-2xl">
              Money rules, tax details and market regulation change over time. This page reflects each book's
              current, verified understanding as of its latest edition and links to the official sources for
              anything that may since have changed.
            </p>
          </HeroFade>
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-[900px] mx-auto px-5 sm:px-8 py-16 md:py-20 space-y-16">
          {LEVEL3_BOOKS.map((b) => (
            <div key={b.slug} id={`book-${b.book_number}`} data-testid={`level3-updates-book-${b.book_number}`}>
              <Reveal>
                <div className="text-[11px] uppercase tracking-[0.16em] font-semibold" style={{ color: b.accent }}>{b.label}</div>
                <h2 className="mt-2 font-serif-d text-[22px] sm:text-[26px] leading-[1.15]" style={{ color: L.navy }}>{b.title}</h2>
                {b.disclaimer && <p className="mt-2 text-[13px] italic leading-[1.6]" style={{ color: L.textMuted }}>{b.disclaimer}</p>}
              </Reveal>
              {BOOK_RESOURCES[b.slug] && (
                <div className="mt-5 space-y-3">
                  {BOOK_RESOURCES[b.slug].map((r) => (
                    <div key={r.name} className="border rounded-sm p-4 flex items-start justify-between gap-4" style={{ borderColor: `${L.navy}14` }}>
                      <div>
                        <div className="text-[14px] font-semibold" style={{ color: L.navy }}>{r.name}</div>
                        <p className="mt-1 text-[13px] leading-[1.55]" style={{ color: L.textMuted }}>{r.detail}</p>
                      </div>
                      <a href={r.url} target="_blank" rel="noopener noreferrer" data-testid={`level3-updates-link-${b.slug}-${r.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="shrink-0 inline-flex items-center gap-1.5 text-[12px] font-semibold uppercase tracking-wide" style={{ color: L.teal }}>
                        Visit <ExternalLink size={12} />
                      </a>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      <section style={{ backgroundColor: L.cream }}>
        <div className="max-w-[900px] mx-auto px-5 sm:px-8 py-16 md:py-20">
          <Reveal>
            <div className="border rounded-sm p-6 sm:p-7 bg-white" style={{ borderColor: `${L.navy}14` }}>
              <div className="font-serif-d text-[18px]" style={{ color: L.navy }}>Noticed something out of date?</div>
              <p className="mt-2 text-[14px] leading-[1.65]" style={{ color: L.textMuted }}>
                Tell us and we'll review it. This page is maintained separately from the books so we can keep it
                current without reprinting anything.
              </p>
              <a data-testid="level3-updates-report-email" href={`mailto:${LEVEL3_SUPPORT.email}?subject=Level 3 Series — outdated information`} className="mt-4 inline-flex items-center gap-2 text-[13px] font-semibold uppercase tracking-wide underline underline-offset-2" style={{ color: L.navy }}>
                <Mail size={14} /> {LEVEL3_SUPPORT.email}
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
